import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  ElementRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AssessmentPayload, IDetails } from '@interfaces/index';
import { environment } from '@environments/environment';
import { firstValueFrom } from 'rxjs';
import { RespectService } from '../../services/respect.service';
import { AppService } from '@ui-components/services';
import { OktaService } from '../../../../../ui-components/src/services/okta.service';
import { ConsentService } from '@ui-components/consent';

@Component({
  selector: 'app-respect',
  templateUrl: './respect.component.html',
  styleUrl: './respect.component.scss'
})
export class RespectComponent implements OnInit {
  form: FormGroup;
  token: string | null = null;
  assessment: IDetails | null = null;
  loading = false;
  valid_token = false;
  outOfInventory = false;
  inviteType: 'marketing' | 'invite' | null = null;
  @Output() isIntersecting = new EventEmitter<boolean>();
  private observer: IntersectionObserver | null = null;
  protected oktaButtonText: string = "Start with Okta";
  readonly showOkta = !environment.production;

  constructor(
    private readonly _app_service: AppService,
    private readonly _respect_service: RespectService,
    protected oktaService: OktaService,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _fb: FormBuilder,
    private _el: ElementRef,
    public consent: ConsentService
  ) {
    this.form = this._fb.group({
      first_name: [null, Validators.required],
      last_name: [null, Validators.required],
      email_address: [null, [Validators.required, Validators.email, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$')]],
      org_name: [null],
    });
  }

  async ngOnInit() {
    this._watchElementForEntry();
    this.loading = true;
    this.token = this._route.snapshot.params['token'];

    if (!this.token) {
      this.loading = false;
      return;
    }

    await this.fetchAssessmentDetails();
  }

  private _watchElementForEntry(): void {
    const options: IntersectionObserverInit = {
      root: null, // defaults to the browser viewport
      rootMargin: '0px',
      threshold: 0.1, // trigger when 10% of the element is visible
    };

    const callback = (entries: IntersectionObserverEntry[]): void => {
      entries.forEach(entry => {
        this.isIntersecting.emit(entry.isIntersecting);

        if (entry.isIntersecting) {
          if (!this.showOkta) {
            this.observer?.unobserve(entry.target);
            return;
          }

          this.oktaService.setAuthState().then(() => {
            if (this.oktaService.getIsAuthenticated()) {
              this.form.patchValue({
                  email_address: this.oktaService.getEmailAddress(),
                  first_name: this.oktaService.getFirstName(),
                  last_name: this.oktaService.getLastName(),
              });
              this.oktaButtonDisplay();
            }
            this.observer?.unobserve(entry.target);
          });
        }
      });
    };

    this.observer = new IntersectionObserver(callback, options);
    this.observer.observe(this._el.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect(); // clean up the observer to prevent memory leaks
    }
  }

  private async fetchAssessmentDetails(): Promise<void> {
    const response = await firstValueFrom(this._respect_service.fetchAssessmentDetails(this.token as string));

    if (!response?.data?.assessment) {
      this.loading = false;
      return;
    }

    this.assessment = response.data.assessment;
    this.valid_token = response.code === 200;
    this.inviteType = response.data.type as 'marketing' | 'invite';
    this.loading = false;
  }

  async oktaButtonDisplay() {
    if (!this.showOkta) {
      return;
    }

    if (this.oktaService.getIsAuthenticated()) {
      this.oktaButtonText = "Okta enabled";
    } else {
      this.oktaButtonText = "Start with Okta";
    }
  }

  async oktaLogin() {
    if (!this.showOkta) {
      return;
    }

    this.oktaService.setRedirectUrl(window.location.href);
    window.location.href = '/okta';
  }

  async loginWithOkta(): Promise<void> {
    if (!this.showOkta) {
      return;
    }

    this.oktaService.setRedirectUrl(window.location.href);
    await this.oktaService.setAuthState();
    if (this.oktaService.getIsAuthenticated()) {
      this.oktaService.loginUserFromOkta();
    }
  }

  async startAssessment(): Promise<void> {
    if (!this.inviteType || !this.token) {
      return;
    }

    const emailControl = this.form.get('email_address');
    if (emailControl?.invalid) {
      emailControl.markAsTouched();
      this._app_service.notify('Please enter a valid email before saving.', 'error');
      return;
    };

    const payload: AssessmentPayload = {
      ...this.form.value,
      token: this.token,
      org_name: this.form.value.org_name ?? ''
    };

    await this.fetchAccessToken(payload);
  }

  private async fetchAccessToken(payload: AssessmentPayload): Promise<void> {
    const request$ = this.inviteType === 'marketing'
      ? this._respect_service.registerMarketingUser(payload)
      : this._respect_service.startAssessmentWithInvite(payload);

    const response = await firstValueFrom(request$);

    if (response?.data?.access_token) {
      this.outOfInventory = false;
      this._router.navigate(['/assessments', 'complete', response.data.access_token]);
      return;
    }

    if (response?.code === 409) {
      this.outOfInventory = true;
      return;
    }

    this.valid_token = false;
  }
}
